// SPDX-License-Identifier: Apache-2.0
import DragManager from '../../components/treeview/tools/dragmanager';
import ApplicationLifeCycleManager from '../app/lifecyclemanager';
import AuthManager from '../auth/authmanager';
import PluginManager from '../auth/pluginmanager';
import ConfigManager from '../configuration/configmanager';
import ErrorManager from '../error/errormanager';
import I18nManager from '../i18n/i18nmanager';
import LayerManager from '../layers/layermanager';
import SnapManager from '../layers/snapmanager';
import LocalFileManager from '../localfile/localfilemanager';
import LogManager from '../logging/logmanager';
import OfflineManager from '../offline/offlinemanager';
import OgcApiFeaturesManager from '../ogcapi/ogcapifeaturesmanager';
import OnBoardingManager from '../onboarding/onboardingmanager';
import OrderingManager from '../ordering/orderingmanager';
import SessionManager from '../share/sessionmanager';
import ShareManager from '../share/sharemanager';
import StateSerializer from '../share/stateserializer';
import ComponentManager from '../state/componentManager';
import MapManager from '../state/mapManager';
import StateManager from '../state/statemanager';
import UserInteractionManager from '../state/userInteractionManager';
import CustomThemesManager from '../themes/customthemesmanager';
import ThemesHelper from '../themes/themeshelper';
import ThemesManager from '../themes/themesmanager';
import UserLayerManager from '../themes/userlayermanager';
import PermalinkManager from '../url/permalinkmanager';
import UrlManager from '../url/urlmanager';
import UserDataManager from '../userdata/userdatamanager';
import WfsManager from '../wfs/wfsmanager';
import WmsManager from '../wms/wmsmanager';
import WmtsManager from '../wmts/wmtsmanager';
import SelectionManager from '../selection/selectionmanager';
import IGirafeContext from './icontext';
import ThemeFavoritesManager from '../themes/themefavoritesmanager';
import SearchManager from '../search/searchmanager';
import FeedbackManager from '../feedback/feedbackmanager';
import GirafeSingleton from '../../base/GirafeSingleton';

type ManagerConstructor = new (context: IGirafeContext) => GirafeSingleton;
type HookFunction = () => Promise<void>;

export default class ContextInitializationHelper {
  // NOTE: This order is important, because the singletons will be initialized in this order
  // And some singletons need other ones !
  private readonly Managers: [string, ManagerConstructor][] = [
    ['componentManager', ComponentManager],
    ['userDataManager', UserDataManager],
    ['configManager', ConfigManager],
    ['logManager', LogManager],
    ['stateManager', StateManager],
    ['orderingManager', OrderingManager],
    ['applicationLifeCycleManager', ApplicationLifeCycleManager],
    ['mapManager', MapManager],
    ['offlineManager', OfflineManager],
    ['snapManager', SnapManager],
    ['authManager', AuthManager],
    ['pluginManager', PluginManager],
    ['i18nManager', I18nManager],
    ['userInteractionManager', UserInteractionManager],
    ['urlManager', UrlManager],
    ['dragManager', DragManager],
    ['permalinkManager', PermalinkManager],
    ['layerManager', LayerManager],
    ['userLayerManager', UserLayerManager],
    ['themesHelper', ThemesHelper],
    ['stateSerializer', StateSerializer],
    ['sessionManager', SessionManager],
    ['shareManager', ShareManager],
    ['wfsManager', WfsManager],
    ['errorManager', ErrorManager],
    ['customThemesManager', CustomThemesManager],
    ['themesManager', ThemesManager],
    ['wmsManager', WmsManager],
    ['wmtsManager', WmtsManager],
    ['selectionManager', SelectionManager],
    ['localFileManager', LocalFileManager],
    ['ogcApiFeaturesManager', OgcApiFeaturesManager],
    ['onBoardingManager', OnBoardingManager],
    ['themeFavoritesManager', ThemeFavoritesManager],
    ['searchManager', SearchManager],
    ['feedbackManager', FeedbackManager]
  ];

  public constructContext(context: IGirafeContext, ignoreProperties?: string[]) {
    // Create all singletons
    for (const [name, ctor] of this.Managers) {
      if (!ignoreProperties?.includes(name)) {
        (context as unknown as Record<string, GirafeSingleton>)[name] = new ctor(context);
      }
    }
  }

  public async initializeContext(context: IGirafeContext) {
    // Initialize all singletons
    for (const [name] of this.Managers) {
      // If there are some local Hook method that was defined,
      // we call it instead of the default initializeSingleton() method.
      const hook = (context as unknown as Record<string, HookFunction>)[`${name}Hook`];
      if (hook) {
        await hook.call(context);
      } else {
        // Call the standard initializeSingleton() method.
        await (context as unknown as Record<string, GirafeSingleton>)[name].initializeSingleton();
      }
    }
  }
}
